import { useState, type FormEvent } from "react";
import { sameLanguage } from "@gk/shared";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, t } from "../../lib/api";
import { fmtDate, fmtDuration, useAsync } from "../../lib/hooks";
import { useSession } from "../../lib/session";
import { Empty, ErrorNote, Modal, StatusPill } from "../../components/ui";
import type { Engagement } from "../Engagement";
import { PersonForm, type Person } from "./Plan";

interface Row {
  id: string;
  status: string;
  startedAt: string | null;
  recordedSeconds: number;
  interviewerLang: string;
  intervieweeLang: string;
  pseudonym: string;
  profile: { department?: string; designation?: string; location?: string };
  gaps: number;
  mode: "two_phone" | "record";
  participantCount: number;
}

export function InterviewsTab({ engagement }: { engagement: Engagement }) {
  const { tenant } = useParams();
  const nav = useNavigate();
  const list = useAsync(() => api.get<Row[]>(`${t(tenant!)}/engagements/${engagement.id}/interviews`), [tenant, engagement.id]);
  const [creating, setCreating] = useState(false);
  return (
    <div className="stack">
      <div className="row">
        <p className="muted">Each interview is recorded, analysed, then reviewed and verified before it reaches the process map.</p>
        <span className="spacer" />
        {engagement.role !== "reviewer" && (
          <button className="btn primary" onClick={() => setCreating(true)}>
            Set up an interview
          </button>
        )}
      </div>
      <ErrorNote error={list.error} />
      <div className="panel flush">
        {list.data?.length === 0 && <Empty title="No interviews yet">Set up the first one, then open it on the interviewer's phone.</Empty>}
        {!!list.data?.length && (
          <table className="table">
            <thead>
              <tr>
                <th>Participant</th>
                <th>Department and location</th>
                <th>Date</th>
                <th>Length</th>
                <th>Gaps found</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {list.data.map((r) => (
                <tr key={r.id} className="click" onClick={() => nav(`/w/${tenant}/i/${r.id}`)}>
                  <td>
                    <b>{r.pseudonym}</b>
                    {r.participantCount > 1 && <span className="muted small"> and {r.participantCount - 1} more</span>}
                    <div className="muted xs">
                      {[r.profile.designation, r.mode === "record" ? (r.participantCount > 1 ? "Group, one phone" : "One phone") : null].filter(Boolean).join(" · ")}
                    </div>
                  </td>
                  <td>{[r.profile.department, r.profile.location].filter(Boolean).join(", ") || "—"}</td>
                  <td>{fmtDate(r.startedAt)}</td>
                  <td>{r.recordedSeconds ? fmtDuration(r.recordedSeconds) : "—"}</td>
                  <td>{r.gaps || "—"}</td>
                  <td>
                    <StatusPill status={r.status} />
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {(r.status === "planned" || r.status === "live") && (
                      <Link className="btn small primary" to={`/field/${tenant}/${r.id}`}>
                        {r.status === "live" ? "Rejoin" : "Start"}
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {creating && <NewInterview engagement={engagement} onClose={() => setCreating(false)} onCreated={(id) => nav(`/field/${tenant}/${id}`)} />}
    </div>
  );
}

function NewInterview({ engagement, onClose, onCreated }: { engagement: Engagement; onClose: () => void; onCreated: (id: string) => void }) {
  const { tenant } = useParams();
  const { config } = useSession();
  const people = useAsync(() => api.get<Person[]>(`${t(tenant!)}/engagements/${engagement.id}/interviewees`), [tenant, engagement.id]);
  const [personId, setPersonId] = useState<string>("");
  const [adding, setAdding] = useState(false);
  const [interviewerLang, setInterviewerLang] = useState(engagement.interviewerLanguages[0]!);
  const [intervieweeLang, setIntervieweeLang] = useState(engagement.intervieweeLanguages[0]!);
  const [error, setError] = useState<Error | null>(null);
  // Same language on both sides: no translation needed, so one phone recording the room is the natural default.
  const [modeChoice, setModeChoice] = useState<"two_phone" | "record" | null>(null);
  const mode = modeChoice ?? (sameLanguage(interviewerLang, intervieweeLang) ? "record" : "two_phone");
  const [others, setOthers] = useState<string[]>([]);
  const name = (c: string) => config.languages.find((l) => l.code === c);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const r = await api.post<{ id: string }>(`${t(tenant!)}/engagements/${engagement.id}/interviews`, {
        intervieweeId: personId,
        interviewerLang,
        intervieweeLang,
        mode,
        participantIds: mode === "record" ? others.filter((o) => o !== personId) : [],
      });
      onCreated(r.id);
    } catch (err) {
      setError(err as Error);
    }
  };

  const pair = [name(interviewerLang), name(intervieweeLang)];
  const weak = pair.some((l) => l && l.support !== "excellent");

  return (
    <Modal title="Set up an interview" onClose={onClose}>
      {adding ? (
        <PersonForm
          engagement={engagement}
          onDone={async (id) => {
            await people.reload();
            if (id) setPersonId(id);
            setAdding(false);
          }}
        />
      ) : (
        <form className="stack" onSubmit={submit}>
          <label className="field">
            <span>Participant</span>
            <div className="row">
              <select className="input" value={personId} onChange={(e) => {
                setPersonId(e.target.value);
                const p = people.data?.find((x) => x.id === e.target.value);
                if (p) setIntervieweeLang(p.language);
              }} required>
                <option value="">Choose a participant…</option>
                {(people.data ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.displayName ? `${p.displayName} (${p.pseudonym})` : p.pseudonym} — {[p.profile.designation, p.profile.department, p.profile.location].filter(Boolean).join(", ")}
                  </option>
                ))}
              </select>
              <button type="button" className="btn" onClick={() => setAdding(true)}>
                Add person
              </button>
            </div>
          </label>
          <fieldset className="field" style={{ border: 0, padding: 0, margin: 0 }}>
            <span>How will you record?</span>
            <div className="mode-pick">
              <label className={`mode-card ${mode === "two_phone" ? "on" : ""}`}>
                <input type="radio" name="mode" checked={mode === "two_phone"} onChange={() => setModeChoice("two_phone")} />
                <b>Two phones, live translation</b>
                <span className="small muted">Each person has a phone and hears the other in their own language. One-to-one.</span>
              </label>
              <label className={`mode-card ${mode === "record" ? "on" : ""}`}>
                <input type="radio" name="mode" checked={mode === "record"} onChange={() => setModeChoice("record")} />
                <b>One phone, record only</b>
                <span className="small muted">Your phone records the conversation, one-to-one or a group. Who said what is worked out afterwards.</span>
              </label>
            </div>
          </fieldset>
          {mode === "record" && (people.data?.length ?? 0) > 1 && (
            <fieldset className="field" style={{ border: 0, padding: 0, margin: 0 }}>
              <span>Others taking part (for a group)</span>
              <div className="stack" style={{ gap: 4, maxHeight: 180, overflowY: "auto" }}>
                {(people.data ?? [])
                  .filter((p) => p.id !== personId)
                  .map((p) => (
                    <label key={p.id} className="check small">
                      <input
                        type="checkbox"
                        checked={others.includes(p.id)}
                        disabled={!others.includes(p.id) && others.length >= 11}
                        onChange={(e) => setOthers(e.target.checked ? [...others, p.id] : others.filter((x) => x !== p.id))}
                      />
                      {p.displayName ? `${p.displayName} (${p.pseudonym})` : p.pseudonym} — {[p.profile.designation, p.profile.department].filter(Boolean).join(", ")}
                    </label>
                  ))}
              </div>
            </fieldset>
          )}
          <div className="grid2">
            <label className="field">
              <span>You speak</span>
              <select className="input" value={interviewerLang} onChange={(e) => setInterviewerLang(e.target.value)}>
                {engagement.interviewerLanguages.map((c) => (
                  <option key={c} value={c}>
                    {name(c)?.name ?? c}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>They speak</span>
              <select className="input" value={intervieweeLang} onChange={(e) => setIntervieweeLang(e.target.value)}>
                {[...new Set([...engagement.intervieweeLanguages, ...engagement.interviewerLanguages])].map((c) => (
                  <option key={c} value={c}>
                    {name(c)?.name ?? c}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {weak && (
            <p className="note-box">
              {pair.filter((l) => l && l.support !== "excellent").map((l) => l!.name).join(" and ")} support is good rather than excellent today
              {pair.some((l) => l?.sttStatus === "preview") ? " (speech recognition is in preview)" : ""}. Speak clearly and check the transcript carefully in review.
            </p>
          )}
          {name(intervieweeLang)?.ttsNote && <p className="muted small">{name(intervieweeLang)!.ttsNote}</p>}
          <ErrorNote error={error} />
          <div className="row">
            <span className="spacer" />
            <button type="button" className="btn" onClick={onClose}>
              Cancel
            </button>
            <button className="btn primary">Open on this phone</button>
          </div>
        </form>
      )}
    </Modal>
  );
}
